import { useTranslation } from "react-i18next";
import type { LucideIcon } from "lucide-react";
import { Box, CircleAlert, Eye, Folder, ListTree, Printer, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipTrigger } from "@/components/ui/tooltip";

/** The side panels the rail can flip open (mutually exclusive — activity-bar style). */
export type ViewerPanel = "contents" | "report";

/**
 * The manual viewer's persistent left **activity rail** (ADR-002) — always visible on the start page, the player AND
 * (later) the authoring editor. It replaces the old header "toggle a drawer" affordance: each icon flips its docked
 * side panel open/closed (clicking the active one collapses it), the selected icon showing as the brand-primary chip
 * (our selected-state convention, inverts per theme). Preflight is off, so the underlying `<button>`/`<nav>` come from
 * the `Button` primitive (UA styles already reset there).
 *
 * View mode surfaces **Contents** (the sections/steps tree) + **Report**. Edit mode (`mode="edit"`, ADR-002 Phase 10)
 * additionally surfaces the authoring cluster — 3D objects / preview / assets / settings — rendered disabled here until
 * the editor that drives them ships.
 */
export function ManualViewerRail({
  active,
  onSelect,
  hasContents,
  canReport,
  mode = "view",
  onPrint,
  printing = false,
}: {
  active: ViewerPanel | null;
  onSelect: (panel: ViewerPanel) => void;
  hasContents: boolean;
  canReport: boolean;
  mode?: "view" | "edit";
  /** Print the manual (ADR-031) — offered for a published, not-archived manual; absent ⇒ no Print tool. */
  onPrint?: () => void;
  /** A print-out is being prepared (the fresh body read + image settling) — the tool is disabled meanwhile. */
  printing?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <nav
      aria-label={t("viewer.manualTools")}
      // A FIXED dark-navy chrome surface in both themes (`--rail`, an IDE-style activity bar) with near-white icons, so
      // the persistent rail reads as distinct nav and stays legible even when the content is switched to light.
      className="border-border bg-rail text-rail-foreground flex w-14 shrink-0 flex-col items-center gap-1.5 border-r py-3"
    >
      <RailButton
        icon={ListTree}
        label={t("viewer.contents")}
        isActive={active === "contents"}
        isDisabled={!hasContents}
        onPress={() => onSelect("contents")}
      />
      {onPrint ? <RailButton icon={Printer} label={printing ? t("viewer.preparingPrint") : t("other.printButtonLabel")} isDisabled={printing} onPress={onPrint} /> : null}
      {canReport ? (
        <RailButton
          icon={CircleAlert}
          label={t("viewer.reportAProblem")}
          isActive={active === "report"}
          onPress={() => onSelect("report")}
        />
      ) : null}

      {mode === "edit" ? (
        <>
          <span className="bg-border my-1 h-px w-6" aria-hidden="true" />
          {/* Authoring tools (ADR-002 Phase 10) — wired when the editor lands; disabled placeholders keep the layout stable. */}
          <RailButton icon={Box} label={t("viewer.3dObjectsComingSoon")} isDisabled />
          <RailButton icon={Eye} label={t("viewer.previewComingSoon")} isDisabled />
          <RailButton icon={Folder} label={t("viewer.assetsComingSoon")} isDisabled />
          <RailButton icon={Settings} label={t("viewer.settingsComingSoon")} isDisabled />
        </>
      ) : null}
    </nav>
  );
}

/** One rail affordance — exported so the authoring editor's rail (ADR-002 Phase 10) reuses the exact chip/hover/tooltip
 *  treatment instead of re-deriving it. */
export function RailButton({
  icon: Icon,
  label,
  isActive = false,
  isDisabled = false,
  onPress,
}: {
  icon: LucideIcon;
  label: string;
  isActive?: boolean;
  isDisabled?: boolean;
  onPress?: () => void;
}) {
  return (
    <TooltipTrigger delay={0}>
      <span className="inline-flex">
        <Button
          variant={isActive ? "default" : "ghost"}
          size="icon-lg"
          aria-label={label}
          aria-pressed={isActive}
          isDisabled={isDisabled}
          onPress={onPress}
          // Inactive icons take the rail's own foreground (dark on the light-grey rail, near-white on the navy), NOT the
          // ghost variant's theme hover. Hover is a `foreground/10` veil — it flips per theme (a dark veil on the light
          // rail, a light veil on the navy), so it's visible on both. Active stays the brand chip (`bg-primary`).
          className={isActive ? undefined : "text-rail-foreground hover:bg-foreground/10 hover:text-rail-foreground"}
        >
          <Icon aria-hidden="true" />
        </Button>
      </span>
      <Tooltip placement="right">{label}</Tooltip>
    </TooltipTrigger>
  );
}
